<template>
  <div data-testid="details-page" class="monthly-view">
    <AlAlert
      type="warning"
      v-if="hasNewAlgorithmVersion || updatingPlanVersion"
    >
      <div class="alert-content">
        <div>
          <p>
            <Localization message="regenerate-message" />
          </p>
          <p>
            <small>
              <Localization message="regenerate-message-warning" />
            </small>
          </p>
        </div>
        <div class="alert-actions">
          <AlLoading v-if="updatingPlanVersion" size="sm" />
          <AlButton
            id="version-regenerate"
            v-if="!updatingPlanVersion"
            size="sm"
            @click="updateVersion"
            ><i class="far fa-check"
          /></AlButton>
          <AlButton
            id="version-dismiss"
            size="sm"
            @click="dismissVersion"
            variant="secondary"
            ><i class="far fa-times"
          /></AlButton>
        </div>
      </div>
    </AlAlert>
    <AlCard class="summary-cards">
      <div data-testid="monthly-cards">
        <h2 class="card-title">
          <Localization message="monthly-summary-card-title" />
        </h2>
        <Carousel :is-loading="isLoading" data-testid="monthly-carousel">
          <template v-for="month in months">
            <MonthlySummary
              :key="month.date.getTime()"
              :active="month === activeMonth"
              :data="month"
              :timezone="displayTimezone"
              :isHours="configuration.unitOfWork !== 'FTE'"
              :status="status"
              :show-na-required="showRequiredUnavailable"
              @click="selectMonth(month)"
              data-testid="monthly-carousel-item"
            />
          </template>
        </Carousel>
      </div>
    </AlCard>
    <MonthChartCard
      v-if="activeMonth"
      v-featureflow:isOn="'capacity-planner-daily-view'"
      :configurationId="configuration._id"
      :timezone="configuration.timezone"
      :month="activeMonth.date"
      :unitOfWork="configuration.unitOfWork"
      :status="status"
    />
    <MonthlyTabs
      v-featureflow:isOn="'capacity-planner-details-tabs'"
      v-if="activeMonth"
      @change="onAdjustmentChange"
    ></MonthlyTabs>
    <Toast v-model="refreshing" type="info" :duration="5000">
      <template v-slot:title>
        <Localization message="refreshing-page" />
      </template>
    </Toast>
  </div>
</template>

<script>
import {
  Carousel,
  Localization,
  Translator,
  Toast,
} from "@agyletime/wfm-vue-common";
import {
  Al2Button as AlButton,
  AlCard,
  AlLoading,
  AlAlert,
} from "@playvox/almendra-components";
import MonthlySummary from "./MonthlySummaryCard.vue";
import MonthChartCard from "./charts/MonthChartCard/MonthChartCard.vue";
import MonthlyTabs from "./formParts/MonthlyTabs.vue";
import { format, isSameMonth } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";

export default {
  name: "DetailsPage",
  components: {
    AlButton,
    AlCard,
    AlLoading,
    Carousel,
    MonthlySummary,
    Localization,
    MonthChartCard,
    MonthlyTabs,
    Toast,
    AlAlert,
  },
  mixins: [Translator],
  props: {
    configuration: {
      type: Object,
      required: true,
    },
    months: {
      type: Array,
      required: true,
    },
    warnings: {
      type: Array,
      required: true,
    },
    showRequiredUnavailable: {
      type: Boolean,
      required: true,
    },
  },
  watch: {
    months() {
      const month = this.$route.params.month;
      if (month) {
        this.activeMonth = this.months.find((m) =>
          isSameMonth(m.date, new Date(month))
        );
      }
    },
  },
  created() {
    const routeMonth = this.$route.params.month;

    if (!routeMonth) {
      this.selectMonth(this.months[0]);
    }

    if (routeMonth) {
      this.activeMonth = this.months.find((m) =>
        isSameMonth(m.date, new Date(routeMonth))
      );
    }
  },
  data() {
    return {
      loading: true,
      generatingCsv: false,
      updatingPlanVersion: false,
      activeMonth: null,
      available: [],
      required: [],
      shrinkage: [],
      leave: [],
      refreshing: false,
    };
  },
  computed: {
    status() {
      return this.configuration?.status || "CREATING_CONFIGURATION";
    },
    displayTimezone() {
      return this.configuration?.timezone;
    },
    isLoading() {
      return !this.configuration?._id;
    },
    algorithmVersion() {
      return parseInt(
        window.PlayvoxWFM.featureflow
          .evaluate("capacity-planner-data-version")
          .value()
      );
    },
    hasDismissedThisVersion() {
      if (!this.configuration) {
        return false;
      }
      return this.algorithmVersion <= this.configuration.dismissedVersion;
    },
    hasNewAlgorithmVersion() {
      if (!this.configuration) {
        return false;
      }

      return (
        this.algorithmVersion > this.configuration.generatedVersion &&
        !this.hasDismissedThisVersion
      );
    },
  },
  methods: {
    async delay(ms) {
      return await new Promise((resolve) => setTimeout(resolve, ms));
    },

    selectMonth(month) {
      if (!month) {
        return;
      }
      this.activeMonth = month;
      const dateInConfigTz = utcToZonedTime(
        this.activeMonth.date,
        this.configuration.timezone
      );
      const dateInConfigTzFormatted = format(dateInConfigTz, "yyyy-MM-dd");

      this.$router.push({
        name: "leave",
        params: {
          ...this.$router.currentRoute.params,
          month: dateInConfigTzFormatted,
        },
      });
    },
    async onAdjustmentChange(data) {
      console.log(data);
    },
    async updateVersion() {
      this.updatingPlanVersion = true;
      const toUpdate = this.configuration;
      toUpdate.generatedVersion = this.algorithmVersion;
      toUpdate.dismissedVersion = this.algorithmVersion;
      await toUpdate.update();
      window.location.reload();
    },
    async dismissVersion() {
      const toUpdate = this.configuration;
      toUpdate.dismissedVersion = this.algorithmVersion;
      await toUpdate.update();
    },
  },
  beforeDestroy() {
    clearInterval(this.pollInterval);
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.card-title {
  margin-bottom: 1rem;
}

.card {
  margin-bottom: 1.5rem;
}

.alert-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.alert-actions {
  margin-top: 0.5rem;
}
.alert-actions > * {
  margin-right: 0.5rem;
}

@media screen and (min-width: 699px) {
  .alert-content {
    flex-direction: row;
    align-items: center;
  }
  .alert-actions {
    display: flex;
    margin-top: 0;
  }
}
</style>

<style>
.dr-alert {
  margin-bottom: 1rem;
}
.dr-alert__messages-container {
  width: 100%;
}
@media screen and (min-width: 699px) {
  .dr-alert__icon-container .dr-alert__icon {
    position: unset;
    height: unset;
    top: unset;
  }
}
</style>
